import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { StadiumsApi } from '@features/stadiums/stadiums.api'
import {
  fetchStadium,
  fetchStadiumDetails,
  fetchStadiumMatches,
  fetchStadiums,
  initializeStadiums,
  setStadium,
  setStadiumMatches,
  setStadiumMatchesFilter,
  setStadiumMatchesPaging,
  setStadiums,
  setStadiumsFilter,
  setStadiumsPaging,
  setStadiumsSorting,
  setStadiumsSortingQuery,
  setStadiumsTrigger,
  updateStadium,
  updateStadiumMatchesFilter,
  updateStadiumMatchesPaging,
  updateStadiumMatchQualifiers,
  updateStadiumsFilter,
  updateStadiumsPaging,
  updateStadiumsSorting,
} from '@features/stadiums/store/actions/stadiums.actions'
import { switchMap } from 'rxjs'
import { map } from 'rxjs/operators'
import { catchRequestError, ToastVariant } from '@performance/ui'
import { concatLatestFrom } from '@ngrx/operators'
import {
  selectStadiumDetail,
  selectStadiumMatchesFilter,
  selectStadiumMatchesPaging,
  selectStadiumsFilter,
  selectStadiumsPaging,
  selectStadiumsSortingQuery,
} from '@features/stadiums/store/selectors/stadiums.selectors'
import {
  StadiumMatchesQuery,
  UpdateStadiumMatchQualifierPayload,
} from '@features/stadiums/stadiums.models'
import { TranslateService } from '@ngx-translate/core'
import { MATCH_QUALIFIER_SUCCESS_TRANSLATION } from '@features/stadium/stadium.constants'
import { ToastStore } from '@core/store/toast/toast.store'

@Injectable()
export class StadiumsEffects {
  readonly #actions$ = inject(Actions)
  readonly #store = inject(Store)
  readonly #api = inject(StadiumsApi)
  readonly #translate = inject(TranslateService)
  readonly #toastStore = inject(ToastStore)

  initializeStadiums$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(initializeStadiums),
      map(() => ({
        filtering: { freeText: '' },
        paging: { recordsRange: { start: 0, end: 10 } },
        pageIncrement: 10,
      })),
      switchMap(({ filtering, paging }) => [
        setStadiumsFilter({ filtering }),
        fetchStadiums({ query: { filtering, paging } }),
      ]),
    ),
  )

  fetchStadiums$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(fetchStadiums),
      concatLatestFrom(() => [
        this.#store.select(selectStadiumsSortingQuery),
        this.#store.select(selectStadiumsPaging),
        this.#store.select(selectStadiumsFilter),
      ]),
      map(([{ query }, sorting, paging, filtering]) =>
        query
          ? query
          : {
              filtering,
              sorting,
              paging,
            },
      ),
      switchMap((query) =>
        this.#api.fetchStadiums(query).pipe(
          map(({ entities: list, paging, sorting }) => ({ list, paging, sorting })),
          catchRequestError(),
        ),
      ),
      switchMap(({ list, paging, sorting }) => [
        setStadiumsPaging({ paging }),
        setStadiumsSorting({ sorting }),
        setStadiums({ list }),
      ]),
    ),
  )

  updateStadiumFilter$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(updateStadiumsFilter),
      concatLatestFrom(() => [
        this.#store.select(selectStadiumsSortingQuery),
        this.#store.select(selectStadiumsPaging),
      ]),
      switchMap(([{ filtering }, sortingQuery, paging]) => [
        setStadiumsFilter({ filtering }),
        fetchStadiums({ query: { filtering, sorting: sortingQuery, paging } }),
      ]),
    ),
  )

  fetchStadium$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(fetchStadium),
      switchMap(({ stadium }) =>
        this.#api.fetchStadium(stadium.venueId).pipe(
          map((selected) => setStadium({ selected })),
          catchRequestError(),
        ),
      ),
    ),
  )

  fetchStadiumDetails$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(fetchStadiumDetails),
      concatLatestFrom(({ stadium }) => [
        this.#store.select(selectStadiumMatchesPaging),
        this.#store.select(selectStadiumMatchesFilter(stadium?.venueId)),
      ]),
      map(([{ stadium }, paging, filtering]) => ({
        stadium,
        matchesQuery: {
          filtering,
          paging,
        },
      })),
      switchMap(({ stadium, matchesQuery }) => [
        fetchStadium({ stadium }),
        updateStadiumMatchesFilter({ matchesFilter: matchesQuery.filtering }),
      ]),
    ),
  )

  updateStadiumsPaging$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(updateStadiumsPaging),
      concatLatestFrom(() => [
        this.#store.select(selectStadiumsSortingQuery),
        this.#store.select(selectStadiumsFilter),
        this.#store.select(selectStadiumsPaging),
      ]),
      map(([{ paginator }, sortingQuery, filtering, paging]) => ({
        sortingQuery,
        filtering,
        paging: {
          totalRecords: paging.totalRecords,
          recordsRange: {
            start: paginator.first,
            end: paginator.first + paginator.rows,
          },
        },
      })),
      map(({ filtering, paging, sortingQuery }) =>
        fetchStadiums({ query: { filtering, sorting: sortingQuery, paging } }),
      ),
    ),
  )

  updateStadiumsSorting$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(updateStadiumsSorting),
      map(({ sorting }) => ({
        column: sorting.field,
        direction: sorting.order === 1 ? 'asc' : 'desc',
      })),
      concatLatestFrom(() => [
        this.#store.select(selectStadiumsFilter),
        this.#store.select(selectStadiumsPaging),
      ]),
      switchMap(([sortingQuery, filtering, paging]) => [
        setStadiumsSortingQuery({ sortingQuery }),
        fetchStadiums({ query: { filtering, sorting: sortingQuery, paging } }),
      ]),
    ),
  )

  updateStadium$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(updateStadium),
      switchMap(({ stadium }) => this.#api.updateStadium(stadium).pipe(catchRequestError())),
      switchMap(() => this.#translate.get('MTR_SUCCESS_STADIUM_UPDATED')),
      map((body) => ({ variant: 'success' as ToastVariant, body, duration: 900000 })),
      switchMap((toast) => {
        this.#toastStore.showToast(toast)
        return [
          fetchStadiums({ query: undefined }),
          setStadiumsTrigger({ trigger: new Date().getTime() }),
        ]
      }),
    ),
  )

  fetchStadiumMatches$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(fetchStadiumMatches),
      concatLatestFrom(() => [
        this.#store.select(selectStadiumMatchesPaging),
        this.#store.select(selectStadiumMatchesFilter()),
      ]),
      map(([{ matchesQuery }, paging, filtering]) =>
        matchesQuery
          ? matchesQuery
          : ({
              filtering,
              paging,
            } as StadiumMatchesQuery),
      ),
      switchMap((matchesQuery) =>
        this.#api.fetchStadiumMatches(matchesQuery).pipe(catchRequestError()),
      ),
      switchMap((response) => [
        setStadiumMatches({ matches: response.matches }),
        setStadiumMatchesPaging({ matchesPaging: response.paging }),
      ]),
    ),
  )

  updateStadiumMatchesFilter$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(updateStadiumMatchesFilter),
      concatLatestFrom(() => [
        this.#store.select(selectStadiumMatchesPaging),
        this.#store.select(selectStadiumDetail),
      ]),
      map(([{ matchesFilter }, paging, matchDetail]) => ({
        filtering: {
          ...matchesFilter,
          venueId: matchesFilter?.venueId ?? matchDetail?.mainInfo?.venueId,
        },
        paging,
      })),
      switchMap((matchesQuery: StadiumMatchesQuery) => [
        setStadiumMatchesFilter({ matchesFilter: matchesQuery.filtering }),
        fetchStadiumMatches({ matchesQuery }),
      ]),
    ),
  )

  updateStadiumMatchesPaging$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(updateStadiumMatchesPaging),
      concatLatestFrom(() => [
        this.#store.select(selectStadiumDetail),
        this.#store.select(selectStadiumMatchesFilter),
        this.#store.select(selectStadiumMatchesPaging),
      ]),
      map(([{ paginator }, stadium, filtering, paging]) => ({
        filtering: {
          ...filtering,
          venueId: stadium?.mainInfo?.venueId,
        },
        paging: {
          totalRecords: paging.totalRecords,
          recordsRange: {
            start: paginator.first,
            end: paginator.first + paginator.rows,
          },
        },
      })),
      map((matchesQuery: StadiumMatchesQuery) => fetchStadiumMatches({ matchesQuery })),
    ),
  )

  updateStadiumMatchQualifiers$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(updateStadiumMatchQualifiers),
      map(({ payload }) => ({
        type: payload.type,
        matchIds: payload.matches.map((m) => m.id),
        isAvailable: payload.qualifier.isAvailable,
        quality: payload.qualifier.quality,
      })),
      switchMap((payload: UpdateStadiumMatchQualifierPayload) =>
        this.#api.updateStadiumMatchQualifier(payload).pipe(
          map(() => ({
            variant: 'success' as ToastVariant,
            i18n: MATCH_QUALIFIER_SUCCESS_TRANSLATION[payload.type],
          })),
          catchRequestError(),
        ),
      ),
      switchMap(({ variant, i18n }) =>
        this.#translate.get(i18n).pipe(map((body) => ({ variant, body }))),
      ),
      switchMap((toast) => {
        this.#toastStore.showToast(toast)
        return [fetchStadiumMatches({ matchesQuery: undefined })]
      }),
    ),
  )
}
